import { z } from 'zod'
import { CUSTOM_THEME, HUES, THEMES } from '@/lib/colour'

export const DEFAULT_WORKSPACE_ID = '00000000-0000-7000-8000-000000000002'
export const DEFAULT_WORKSPACE_SLUG = 'alfieos'
/** The "Everything" scope: no partition filter. */
export const ALL = '*'

/**
 * Core views a workspace can show; Settings is always reachable. The merged
 * ones (calendar, lists, someday, recurring) stay valid in stored records and
 * now mean the app they joined (`MERGED_INTO` in lib/nav.ts).
 */
export const CORE_NAV_KEYS = [
  'today',
  'inbox',
  'upcoming',
  'projects',
  'pages',
  'lists',
  'calendar',
  'review',
  'someday',
  'recurring',
  'log',
  'agents',
  'search',
  'all',
] as const
export type CoreNavKey = (typeof CORE_NAV_KEYS)[number]

export const workspaceSpec = z
  .object({
    name: z.string().min(1).max(60),
    slug: z.string().regex(/^[a-z0-9][a-z0-9-]{0,30}$/, 'lowercase letters, digits and dashes'),
    icon: z.string().max(40).optional(),
    colour: z.enum(HUES).default('sky'),
    packs: z.array(z.string().regex(/^[a-z][a-z0-9-]*$/)).default([]),
    today_cap: z.number().int().min(1).max(20).optional(),
    order: z.number().int().default(0),
    nav: z.array(z.enum(CORE_NAV_KEYS)).optional(),
    theme: z.enum([...THEMES, CUSTOM_THEME]).optional(),
    /** theme "custom": the workspace's own surface colours (see randomPalette) */
    palette: z.record(z.string(), z.string().max(64)).optional(),
    /** the workspace layer's brightness and glass switch (lib/themeLayers.ts); unset passes through to Auto */
    brightness: z.enum(['auto', 'light', 'dark', 'black']).optional(),
    glass: z.boolean().optional(),
    /** the manifest this suite was installed from (suites/index.ts); unset = inferred from its packs */
    suite: z
      .string()
      .regex(/^[a-z][a-z0-9-]*$/)
      .max(30)
      .optional(),
  })
  .strict()
export type WorkspaceSpec = z.infer<typeof workspaceSpec>

export const workspaceStatus = z.object({ archived_at: z.string().optional() })
export type WorkspaceStatus = z.infer<typeof workspaceStatus>
